import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Paper, styled, SxProps, Typography, IconButton } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface ProofOfIdUploadProps {
    currentFile?: string;
    sx?: SxProps;
    preview?: boolean;
    onFileSelect?: (file: File) => void;
    onUpload?: () => Promise<boolean>;
    onDelete?: () => Promise<boolean>;
    loading?: boolean;
    trustDetailsVerified?: boolean;
    disabled?: boolean;
}

const ProofOfIdUpload: React.FC<ProofOfIdUploadProps> = ({
    currentFile,
    sx,
    preview = false,
    onFileSelect,
    onUpload,
    onDelete,
    trustDetailsVerified,
    loading = false,
    disabled = false
}) => {
    const [previewUrl, setPreviewUrl] = useState<string | undefined>(currentFile);
    const [hasNewFile, setHasNewFile] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Update internal state whenever currentFile prop changes
    useEffect(() => {
        setPreviewUrl(currentFile);
        if (!currentFile) {
            setHasNewFile(false);
        }
    }, [currentFile]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const selectedFile = files[0];

            // If there's an existing file and user selects a new one, clear the existing
            if (previewUrl && !hasNewFile) {
                setPreviewUrl(undefined);
            }

            if (onFileSelect) {
                onFileSelect(selectedFile);
            }
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setHasNewFile(true);
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            const droppedFile = files[0];

            // If there's an existing file and user drops a new one, clear the existing
            if (previewUrl && !hasNewFile) {
                setPreviewUrl(undefined);
            }

            if (onFileSelect) {
                onFileSelect(droppedFile);
            }
            setPreviewUrl(URL.createObjectURL(droppedFile));
            setHasNewFile(true);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleClick = () => {
        if (disabled) return;
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    if (!preview) {
        return (
            <Container>
                <PreviewSection sx={sx}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        width: '100%',
                        height: '100%',
                    }}>
                        <UploadFileIcon sx={{ fontSize: 48, color: 'black', mb: 2 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Documents uploaded.
                        </Typography>
                        <OpenFileButton onClick={
                            () => {
                                window.open(previewUrl, '_blank');
                            }
                        }>Open File</OpenFileButton>
                    </Box>
                </PreviewSection>
            </Container>
        );
    }

    return (
        <Container>
            <input
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
                disabled={disabled}
            />

            {previewUrl ? (
                // Preview mode - show file preview with actions
                <PreviewSection>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        width: '100%',
                        height: '100%',
                    }}>
                        {previewUrl?.toLowerCase().endsWith('.pdf') ? (
                            <>
                                <UploadFileIcon sx={{ fontSize: 48, color: 'black', mb: 2 }} />
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    PDF Document ready
                                </Typography>
                            </>
                        ) : (
                            <Box
                                component="img"
                                src={previewUrl}
                                alt="Document preview"
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: '200px',
                                    objectFit: 'contain',
                                    borderRadius: 1,
                                    mb: 2,
                                }}
                            />
                        )}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {hasNewFile ? (
                                // For newly selected files, show Delete
                                <ActionIconButton
                                    color="error"
                                    onClick={() => {
                                        setPreviewUrl(undefined);
                                        setHasNewFile(false);
                                    }}
                                    disabled={loading || disabled}
                                    title="Delete"
                                >
                                    <DeleteIcon />
                                </ActionIconButton>
                            ) : (
                                // For existing uploaded files, show Delete with onDelete callback
                                onDelete && (
                                    <ActionIconButton
                                        color="error"
                                        onClick={async () => {
                                            const success = await onDelete();
                                            if (success) {
                                                setPreviewUrl(undefined);
                                                setHasNewFile(false);
                                            }
                                        }}
                                        disabled={loading || disabled}
                                        title="Delete"
                                    >
                                        <DeleteIcon />
                                    </ActionIconButton>
                                )
                            )}

                            {hasNewFile && onUpload && (
                                <ActionIconButton
                                    color="primary"
                                    onClick={onUpload}
                                    disabled={loading || disabled}
                                    title="Upload"
                                >
                                    <CheckIcon />
                                </ActionIconButton>
                            )}

                            <ActionIconButton
                                color="primary"
                                onClick={handleClick}
                                title="Change File"
                                disabled={trustDetailsVerified || disabled}
                            >
                                <EditIcon />
                            </ActionIconButton>

                            <ActionIconButton
                                onClick={() => {
                                    window.open(previewUrl, '_blank');
                                }}
                                title="Open File"
                            >
                                <OpenInNewIcon />
                            </ActionIconButton>
                        </Box>
                    </Box>
                </PreviewSection>
            ) : (
                // Upload mode - show drag and drop
                <UploadSection
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={handleClick}
                >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Drop or upload
                    </Typography>
                    <UploadFileIcon sx={{ fontSize: 48, color: 'black', mb: 2 }} />

                    <Typography variant="caption" color="primary" sx={{ mb: 3 }}>
                        PNG OR JPEG FORMATS
                    </Typography>
                    <BrowseButton variant="contained">
                        Browse files
                    </BrowseButton>
                </UploadSection>
            )}
        </Container>
    );
};

const Container = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '20px',
    width: '100%',
    minHeight: '328px',
    maxHeight: '328px',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));

const UploadSection = styled(Paper)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    boxShadow: 'none',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(10),
    border: '2px dashed #ccc',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#f2f2f2',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    minHeight: 150,
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#f8f8f8',
    },
}));

const PreviewSection = styled(Paper)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    boxShadow: 'none',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#999999',
    minHeight: 328,
    height: 328,
    overflow: 'hidden',
}));

const BrowseButton = styled(Button)(() => ({
    backgroundColor: 'black',
    color: 'white',
    '&:hover': {
        backgroundColor: '#333',
    },
    borderRadius: '16px',
    padding: '8px 24px',
}));

const OpenFileButton = styled(Button)(() => ({
    backgroundColor: 'black',
    color: 'white',
    '&:hover': {
        backgroundColor: '#333',
    },
    borderRadius: '16px',
    padding: '8px 24px',
}));

const ActionIconButton = styled(IconButton)(() => ({
    backgroundColor: '#f5f5f5',
    border: 'none',
    borderRadius: '25px',
    padding: '12px',
    '&:hover': {
        backgroundColor: '#e0e0e0',
    },
    '&.MuiIconButton-colorPrimary': {
        backgroundColor: '#f5f5f5',
        color: 'black',
        '&:hover': {
            backgroundColor: '#333',
        },
    },
    '&.MuiIconButton-colorError': {
        backgroundColor: '#f5f5f5',
        color: 'black',
        '&:hover': {
            backgroundColor: '#d32f2f',
        },
    },
    '&:disabled': {
        backgroundColor: '#f0f0f0',
        color: '#ccc',
    },
}));

export default ProofOfIdUpload;